import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as XLSX from "xlsx";
import { useGetWorkOrderQuery } from "../../../Api/attoDeskApi";
import { IQuoteWorkOrder } from "../../../Api/Interface/api.interface";
import { WorkOrderStatus } from "../../../Core/Enum/enum";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Pagination from "../../../Components/Pagination";
import { CiSearch } from "react-icons/ci";
import BlockIcon from "@mui/icons-material/Block";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import VisibilitySharpIcon from "@mui/icons-material/VisibilitySharp";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";

const hdrSx = {
  fontWeight: 700,
  fontSize: 11,
  color: "#64748b",
  textTransform: "uppercase" as const,
  letterSpacing: "0.06em",
  bgcolor: "#f8fafc",
  py: 1.5,
  px: 2,
  borderBottom: "1px solid #e2e8f0",
  whiteSpace: "nowrap" as const,
};
const cellSx = {
  fontSize: 12,
  color: "#1e293b",
  py: 1.25,
  px: 2,
  borderBottom: "1px solid #f1f5f9",
};

const cap = (s?: string | null) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : "";

// ── Reason detection ──────────────────────────────────────────────────────────
type TerminateReason = "terminated" | "client_deleted" | "data_missing";

const getReasons = (row: IQuoteWorkOrder): TerminateReason[] => {
  const reasons: TerminateReason[] = [];
  if (row.quoteWorkOrderStatus === WorkOrderStatus.Cancel) reasons.push("terminated");
  if (!row.customer || (!row.customer.customerFirstName && !row.customer.customerCompanyName))
    reasons.push("client_deleted");
  if (!row.quoteWorkOrderNo) reasons.push("data_missing");
  return reasons;
};

const ReasonChip = ({ reason }: { reason: TerminateReason }) => {
  const cfg = {
    terminated:    { label: "Terminated",    color: "#dc2626", bg: "#fef2f2", border: "#fecaca", icon: <BlockIcon sx={{ fontSize: 11 }} /> },
    client_deleted:{ label: "Client Deleted", color: "#d97706", bg: "#fffbeb", border: "#fde68a", icon: <PersonOffOutlinedIcon sx={{ fontSize: 11 }} /> },
    data_missing:  { label: "Data Missing",  color: "#2f3891", bg: "#f5f3ff", border: "#ddd6fe", icon: <WarningAmberOutlinedIcon sx={{ fontSize: 11 }} /> },
  }[reason];
  return (
    <Chip
      icon={cfg.icon}
      label={cfg.label}
      size="small"
      sx={{
        height: 20, fontSize: 10, fontWeight: 700,
        color: cfg.color, bgcolor: cfg.bg,
        border: `1px solid ${cfg.border}`,
        "& .MuiChip-icon": { color: cfg.color, ml: "6px" },
      }}
    />
  );
};

// ── Summary card ──────────────────────────────────────────────────────────────
const SummaryCard = ({
  icon, title, value, color,
}: {
  icon: React.ReactNode; title: string; value: string | number; color: string;
}) => (
  <Card sx={{ borderRadius: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9", overflow: "hidden" }}>
    <Box sx={{ height: 3, bgcolor: color }} />
    <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box sx={{ width: 40, height: 40, borderRadius: 2, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: `${color}18`, flexShrink: 0 }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6 }}>{title}</Typography>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a", lineHeight: 1.2, mt: 0.25 }}>{value}</Typography>
      </Box>
    </Box>
  </Card>
);

// ── Component ─────────────────────────────────────────────────────────────────
const TerminatePage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [pdfLoading, setPdfLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (searchQuery.trim()) params.search = searchQuery.trim();
      const response = await axios.get(`${API_URL}/pdf/terminated-quotes-report`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `terminated-quotes-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { /* silent */ } finally { setPdfLoading(false); }
  };
  const rowsPerPage = 15;

  const { data, isLoading } = useGetWorkOrderQuery({ searchText: "", id: "" });

  const allRows: IQuoteWorkOrder[] = Array.isArray(data?.data)
    ? (data!.data as IQuoteWorkOrder[])
    : [];

  // Keep rows that are terminated OR have missing client/data
  const terminatedRows = allRows.filter((row) => {
    const isTerminated = row.quoteWorkOrderStatus === WorkOrderStatus.Cancel;
    const isClientMissing =
      !row.customer ||
      (!row.customer.customerFirstName && !row.customer.customerCompanyName);
    const isDataMissing = !row.quoteWorkOrderNo;
    return isTerminated || isClientMissing || isDataMissing;
  });

  const q = searchQuery.trim().toLowerCase();
  const filtered = terminatedRows.filter((row) => {
    if (!q) return true;
    const quoteNo = (row.quoteWorkOrderNo || "").toLowerCase();
    const name = `${cap(row.customer?.customerFirstName)} ${cap(row.customer?.customerLastName)}`.toLowerCase();
    const company = (row.customer?.customerCompanyName || "").toLowerCase();
    return quoteNo.includes(q) || name.includes(q) || company.includes(q);
  });

  const totalTerminated  = terminatedRows.filter((r) => r.quoteWorkOrderStatus === WorkOrderStatus.Cancel).length;
  const totalClientDel   = terminatedRows.filter((r) => !r.customer || (!r.customer.customerFirstName && !r.customer.customerCompanyName)).length;
  const totalDataMissing = terminatedRows.filter((r) => !r.quoteWorkOrderNo).length;
  const totalAmount = filtered.reduce(
    (s, r) => s + parseFloat(r.quoteWorkOrderSaleGrandTotal || "0"), 0
  );

  const handleExportToExcel = () => {
    const data = filtered.map((row, i) => ({
      "#":          i + 1,
      "Quote No":   row.quoteWorkOrderNo || "",
      "Invoice No": (row as any).InvoiceNo || "",
      "Date":       row.created_at ? dayjs(row.created_at).format("DD MMM YYYY") : "",
      "Client":     row.customer?.customerCompanyName || `${cap(row.customer?.customerFirstName)} ${cap(row.customer?.customerLastName)}`.trim() || "—",
      "Amount":     parseFloat(row.quoteWorkOrderSaleGrandTotal || "0").toFixed(2),
      "Reasons":    getReasons(row).join(", "),
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((k) => ({
      wch: Math.max(k.length, ...data.map((r: any) => String(r[k] || "").length)) + 2,
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Terminated Quotes");
    XLSX.writeFile(wb, `terminated_quotes_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  if (isLoading) return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "40vh" }}>
      <CircularProgress sx={{ color: "#dc2626" }} />
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>

      {/* ── Header ── */}
      <Card sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 16px rgba(0,0,0,0.07)", border: "1px solid #e2e8f0", mb: 3 }}>
        <Box sx={{ height: 4, background: "linear-gradient(90deg,#dc2626,#f97316,#d97706)" }} />
        <Box sx={{ px: 3, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <BlockIcon sx={{ fontSize: 20, color: "#dc2626" }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#0f172a" }}>Terminated Quotes</Typography>
              <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.25 }}>
                Cancelled quotes and records with missing client or data
              </Typography>
            </Box>
          </Box>
          <TextField
            size="small"
            placeholder="Search quote, client…"
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ fontSize: 16, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
            sx={{ width: 240, "& .MuiOutlinedInput-root": { borderRadius: 1.5, fontSize: 13 } }}
          />
          <Button size="small" variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <PrintIcon sx={{ fontSize: 15 }} />}
            onClick={handleDownloadPdf} disabled={pdfLoading}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", borderColor: "#e2e8f0" }}>
            {pdfLoading ? "Generating…" : "Print"}
          </Button>
          {filtered.length > 0 && (
            <Button size="small" variant="outlined"
              startIcon={<TableChartOutlinedIcon sx={{ fontSize: 15 }} />}
              onClick={handleExportToExcel}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#16a34a", borderColor: "#e2e8f0" }}>
              Excel
            </Button>
          )}
        </Box>
      </Card>

      {/* ── Summary cards ── */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<BlockIcon sx={{ fontSize: 18, color: "#dc2626" }} />} title="Terminated" value={totalTerminated} color="#dc2626" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<PersonOffOutlinedIcon sx={{ fontSize: 18, color: "#d97706" }} />} title="Client Deleted" value={totalClientDel} color="#d97706" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<WarningAmberOutlinedIcon sx={{ fontSize: 18, color: "#2f3891" }} />} title="Data Missing" value={totalDataMissing} color="#2f3891" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard icon={<ReceiptLongOutlinedIcon sx={{ fontSize: 18, color: "#64748b" }} />} title="Total Value" value={`$${totalAmount.toFixed(2)}`} color="#64748b" />
        </Grid>
      </Grid>

      {/* ── Table ── */}
      <Card sx={{ borderRadius: 2.5, boxShadow: "0 1px 8px rgba(0,0,0,0.07)", border: "1px solid #f1f5f9" }}>
        {/* Section header */}
        <Box sx={{ px: 2.5, py: 1.75, borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: "#dc2626" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 12, color: "#1e293b", textTransform: "uppercase", letterSpacing: 0.8 }}>
            Terminated Records
          </Typography>
          <Chip label={filtered.length} size="small"
            sx={{ height: 18, fontSize: 10, fontWeight: 700, color: "#dc2626", bgcolor: "#fef2f2", border: "1px solid #fecaca", ml: 0.5 }} />
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {["#", "Quote No", "Invoice No", "Date", "Client", "Amount", "Reason(s)", ""].map((h, i) => (
                  <TableCell key={i} sx={{ ...hdrSx, textAlign: i === 7 ? "right" : "left" }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 6, color: "#94a3b8", fontSize: 13 }}>
                    {searchQuery ? "No matching records found" : "No terminated or deleted records"}
                  </TableCell>
                </TableRow>
              ) : (
                filtered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => {
                    const reasons = getReasons(row);
                    const clientName =
                      row.customer?.customerCompanyName ||
                      `${cap(row.customer?.customerFirstName)} ${cap(row.customer?.customerLastName)}`.trim() ||
                      null;
                    const amount = parseFloat(row.quoteWorkOrderSaleGrandTotal || "0");

                    return (
                      <TableRow key={row.id} sx={{ "&:hover": { bgcolor: "#fff5f5" } }}>
                        <TableCell sx={{ ...cellSx, color: "#94a3b8" }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>

                        <TableCell sx={{ ...cellSx, fontWeight: 700 }}>
                          {row.quoteWorkOrderNo || (
                            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic" }}>
                              No quote number
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell sx={cellSx}>
                          {(row as any).InvoiceNo ? (
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#16a34a" }}>
                              {(row as any).InvoiceNo}
                            </Typography>
                          ) : (
                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>—</Typography>
                          )}
                        </TableCell>

                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontSize: 12, color: "#334155" }}>
                            {row.created_at ? dayjs(row.created_at).format("MMM D, YYYY") : "—"}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                            {row.created_at ? dayjs(row.created_at).format("h:mm A") : ""}
                          </Typography>
                        </TableCell>

                        <TableCell sx={cellSx}>
                          {clientName ? (
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>
                              {clientName}
                            </Typography>
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                              <PersonOffOutlinedIcon sx={{ fontSize: 13, color: "#d97706" }} />
                              <Typography sx={{ fontSize: 11, color: "#d97706", fontStyle: "italic" }}>
                                Client deleted
                              </Typography>
                            </Box>
                          )}
                          {row.customer?.customerEmail && (
                            <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                              {row.customer.customerEmail}
                            </Typography>
                          )}
                        </TableCell>

                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontSize: 12, fontWeight: 600, color: amount > 0 ? "#1e293b" : "#94a3b8" }}>
                            {amount > 0 ? `$${amount.toFixed(2)}` : "—"}
                          </Typography>
                        </TableCell>

                        <TableCell sx={cellSx}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {reasons.map((r) => <ReasonChip key={r} reason={r} />)}
                          </Box>
                          {(() => {
                            const memo: string = (row as any).quoteWorkOrderCustomerMemo || "";
                            const terminateReason = memo.startsWith("__TERMINATE__:")
                              ? memo.slice("__TERMINATE__:".length).trim()
                              : null;
                            return terminateReason ? (
                              <Typography sx={{ fontSize: 11, color: "#64748b", fontStyle: "italic", mt: 0.5 }}>
                                "{terminateReason}"
                              </Typography>
                            ) : null;
                          })()}
                        </TableCell>

                        <TableCell sx={{ ...cellSx, textAlign: "right" }}>
                          {row.id && (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => navigate(`/work-order/history/${row.id}`)}
                              sx={{
                                minWidth: 0, p: "4px 8px", borderRadius: 1.5,
                                borderColor: "#e2e8f0", color: "#64748b",
                                "&:hover": { borderColor: "#cbd5e1", bgcolor: "#f8fafc" },
                              }}
                            >
                              <VisibilitySharpIcon sx={{ fontSize: 14 }} />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end", px: 2, py: 1.5 }}>
          <Pagination
            count={filtered.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(_e, p) => setPage(p)}
          />
        </Box>
      </Card>
    </Box>
  );
};

export default TerminatePage;
